import { apiClient } from '@/auth';

/**
 * User-surveys endpoints — used by the Wellbeing tab to render mindful-
 * eating reflections joined to the meal the patient was reflecting on.
 *
 *   GET /user-surveys/user/{patient_id}/forms/{surveyType}
 *     Returns the form definition (questions + widgets + options).
 *     Cached aggressively — the form rarely changes.
 *
 *   GET /user-surveys/user/{patient_id}/responses
 *     Returns up to 100 responses, newest first. Optional filters:
 *       survey_type            — 'mindful-eating' for our use case
 *       related_activity_type  — 'meal-log' to scope to meal surveys
 *       related_activity_id    — narrow to one meal's response
 *       before_time            — pagination cursor (server returns
 *                                responses older than this timestamp)
 *
 * The responses endpoint has no date_since/date_until filter, so we
 * paginate by `before_time` and join client-side against meals fetched
 * for the date range the page cares about.
 */

export type SurveyWidget =
  | 'input-multicheck'
  | 'input-radio'
  | 'input-scale'
  | 'input-text'
  | 'heading';

export type SurveyFormOption = {
  value: string;
  label: string;
  /** Some option lists carry a small emoji/iconography in a separate
   *  field. Modeled loose because the server schema varies per form. */
  emoji?: string | null;
};

export type SurveyFormItem = {
  /** Stable id used on responses (`form_item_id`). */
  id: string;
  label: string;
  /** Short variant used in compact UI (table column header, etc.). */
  label_short?: string | null;
  widget: SurveyWidget;
  options?: SurveyFormOption[];
  /** Scale-widget bounds; the form may also encode them as the first /
   *  last `options` entries — caller picks whichever is present. */
  scale_min?: number | null;
  scale_max?: number | null;
};

export type SurveyForm = {
  id: string;
  survey_type: string;
  md5: string;
  items: SurveyFormItem[];
};

/**
 * Wire shape of `GET /user-surveys/.../forms/{type}`. The MNT swagger
 * defines it as `UserSurveyFormResponse { id, survey_type, survey_form:
 * { items: [...] }, survey_form_md5, created_time }` — note items are
 * nested inside `survey_form`. We flatten this in `userSurveysApi.form`
 * so callers see the simpler `SurveyForm` shape.
 */
type SurveyFormWire = {
  id: string | number;
  survey_type: string;
  survey_form: { items: SurveyFormItem[] };
  survey_form_md5: string;
};

/** One answer inside a response. The `value` shape depends on widget:
 *    input-multicheck → string[]
 *    input-radio      → string
 *    input-scale      → number
 *    input-text       → string | number
 *    heading          → (no value — won't appear in responses) */
export type SurveyResponseItem = {
  form_item_id: string;
  value: string | string[] | number | null;
};

export type SurveyResponse = {
  id: number;
  user_id: number;
  related_activity_type: 'meal-log' | string;
  related_activity_id: number;
  survey_type: 'mindful-eating' | string;
  survey_form_md5: string;
  survey_responses: { items: SurveyResponseItem[] };
  created_time: string;
  updated_time: string;
};

export type SurveyResponseListParams = {
  surveyType?: string;
  relatedActivityType?: string;
  relatedActivityId?: number;
  /** Pagination cursor — server returns responses older than this. */
  beforeTime?: string;
  /** Server cap is 100. */
  limit?: number;
};

export const userSurveysApi = {
  async form(patientId: number, surveyType: string): Promise<SurveyForm | null> {
    try {
      const res = await apiClient.get<SurveyFormWire>(
        `/user-surveys/user/${patientId}/forms/${surveyType}`,
      );
      if (!res.data) return null;
      // Flatten the wire shape: items are nested inside `survey_form` on
      // the server, but consumers expect them at the root.
      return {
        id: String(res.data.id),
        survey_type: res.data.survey_type,
        md5: res.data.survey_form_md5,
        items: res.data.survey_form?.items ?? [],
      };
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      // 404 = no form configured for this patient/surveyType. Match the
      // graceful-degradation pattern v2 uses elsewhere — treat as "no
      // form" rather than crashing the page.
      if (status === 404) return null;
      throw err;
    }
  },

  async responses(
    patientId: number,
    params: SurveyResponseListParams = {},
  ): Promise<SurveyResponse[]> {
    try {
      const res = await apiClient.get<SurveyResponse[]>(
        `/user-surveys/user/${patientId}/responses`,
        {
          params: {
            survey_type: params.surveyType,
            related_activity_type: params.relatedActivityType,
            related_activity_id: params.relatedActivityId,
            before_time: params.beforeTime,
            limit: params.limit,
          },
        },
      );
      return Array.isArray(res.data) ? res.data : [];
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) return [];
      throw err;
    }
  },
};
